
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { ValidationProvider } from 'vee-validate';
import { PERMISSION } from '@/permissions';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import ExtraPaymentsValidationModal from '@/components/advanced/ExtraPaymentsValidationModal.vue';

const namespace: string = 'auth';

@Component<InvoiceSettings>({
	components: {
		InfoBanner,
		ToggleSwitch,
		ValidationProvider,
		TextButton,
		ExtraPaymentsValidationModal
	}
})
export default class InvoiceSettings extends Vue {
	@Prop({ type: Object, default: () => ({}) }) private targetOrderOptions!: OrderOptions;
	@Getter('hasPermission', { namespace }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('getExtraPaymentMethods', { namespace }) private extraPaymentMethods!: RestaurantExtraPaymentMethod[] | null;
	@Watch('targetOrderOptions')
	private onTargetOrderOptionsChange(): void {
		this.mutableOrderOptions = { ...this.targetOrderOptions };
	}

	private mutableOrderOptions: OrderOptions = { ...this.targetOrderOptions };
	private dueDateEnabled: boolean = typeof this.targetOrderOptions.generic_catering_invoice_due_days === 'number';
	private showValidationModal: boolean = false;

	/**
	 * Whether the validation rules button should be shown: the user has permission and the
	 * location has at least one extra payment method that supports validation rules
	 *
	 * @return {boolean}
	 */
	private get canManageValidationRules(): boolean {
		return this.hasPermission(PERMISSION.SET_PAYMENT_VALIDATION_RULES)
			&& !!this.extraPaymentMethods?.some(
				method => ['cost_center', 'purchase_order'].includes(method.type)
			);
	}

	/**
	 * Handle the due date toggle change.
	 * When turned on, default to 7 days.
	 * When turned off, remove both invoice fields.
	 *
	 * @return {void}
	 */
	private onDueDateToggle(): void {
		if (this.dueDateEnabled) {
			Vue.set(this.mutableOrderOptions, 'generic_catering_invoice_due_days', 7);
		}
		else {
			Vue.delete(this.mutableOrderOptions, 'generic_catering_invoice_due_days');
			Vue.delete(this.mutableOrderOptions, 'generic_catering_invoice_auto_reminder');
		}
		this.updateOrderOptions();
	}

	/**
	 * Remove the auto reminder property if toggle is set to false.
	 *
	 * @return {void}
	 */
	private onReminderToggle(): void {
		if (!this.mutableOrderOptions.generic_catering_invoice_auto_reminder) {
			Vue.delete(this.mutableOrderOptions, 'generic_catering_invoice_auto_reminder');
		}
		this.updateOrderOptions();
	}

	/**
	 * Emit updated order options to the parent.
	 *
	 * @return {void}
	 */
	private updateOrderOptions(): void {
		this.$emit('update-order-options', { ...this.mutableOrderOptions });
	}
}
